import React, { useState } from 'react';
import { 
  Grid, Paper, Typography, TableContainer, Table, TableHead, TableRow, 
  TableCell, TableBody, Box, Switch, IconButton, Dialog, 
  DialogTitle, DialogContent, DialogActions, Button, TextField, 
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import { Edit, AddCircleOutline, DeleteOutline } from '@mui/icons-material';

type ServicioExtended = {
  id?: number;
  tipo: string;
  descripcion: string;
  precioCosto: number;
  precioVenta: number;
  moneda?: string;
  monedaCosto?: string;
  revisado?: boolean;
  revisadoCosto?: boolean;
  nroFile?: string;
  // Campos adicionales para nuevos servicios
  aerolinea?: string;
  destino?: string;
  hotel?: string;
  noches?: number;
  serviciosIncluidos?: string;
  proveedor?: string;
  proveedorId?: number;
  fechaViaje?: string;
};

type Props = {
  servicios: ServicioExtended[];
  monedaPaquete: string;
  fechaViaje?: string;
  formatearMoneda: (valor: number, moneda?: string) => string;
  onUpdateServicio?: (index: number, servicioActualizado: ServicioExtended, recalcularFinanzas?: boolean, soloRevisado?: boolean) => void;
  onAddServicio?: (nuevoServicio: ServicioExtended) => void;
  onPersistCostoEdit?: (index: number, servicioActualizado: ServicioExtended, recalcularFinanzas?: boolean) => Promise<void>;
  onPersistNuevoCosto?: (nuevoServicio: ServicioExtended) => Promise<void>;
  paqueteId?: number;
  proveedores?: Array<{ id: number; nombre: string }>;
  onDeleteServicio?: (index: number, servicio: ServicioExtended) => void;
};

// Opciones predefinidas
const DESTINOS = [
  "Buenos Aires", "Cancún", "Miami", "Nueva York", "Madrid", "Barcelona",
  "París", "Roma", "Londres", "Río de Janeiro", "São Paulo", "Punta Cana",
  "Cartagena", "Santiago de Chile", "Montevideo", "Bogotá", "Lima"
];

const AEROLINEAS = [
  "Aerolíneas Argentinas", "LATAM", "American Airlines", "Copa Airlines",
  "Avianca", "Gol", "JetSmart", "Flybondi", "United Airlines", "Delta"
];

export const ServicesList: React.FC<Props> = ({ 
  servicios = [], 
  monedaPaquete, 
  fechaViaje,
  formatearMoneda,
  onUpdateServicio,
  onAddServicio,
  onPersistCostoEdit,
  onPersistNuevoCosto,
  proveedores = [],
  onDeleteServicio
}) => {
  
  // --- RBAC: determinar si el usuario es admin ---
  const isAdmin = React.useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      return u.rol === "Superadministrador" || u.rol === "Administrador";
    } catch { return false; }
  }, []);
  
  // Estados para modal de edición
  const [modalAbierto, setModalAbierto] = useState(false);
  const [servicioEditando, setServicioEditando] = useState<{
    index: number;
    servicio: ServicioExtended;
    tipo: 'costo' | 'precio';
  } | null>(null);

  // Estados para modal de eliminar
  const [modalEliminarAbierto, setModalEliminarAbierto] = useState(false);
  const [servicioAEliminar, setServicioAEliminar] = useState<{
    index: number;
    servicio: ServicioExtended;
  } | null>(null);

  // Estados para modal de agregar
  const [modalAgregarAbierto, setModalAgregarAbierto] = useState(false);
  const [guardandoModalCosto, setGuardandoModalCosto] = useState(false);
  const [tipoAgregar, setTipoAgregar] = useState<'Aéreo' | 'Hotel' | 'Extra'>('Aéreo');
  const [modoAgregar, setModoAgregar] = useState<'costo' | 'precio'>('costo');
  const [nuevoServicio, setNuevoServicio] = useState<Partial<ServicioExtended>>({
    tipo: 'Aéreo',
    descripcion: '',
    precioVenta: 0,
    precioCosto: 0,
    moneda: 'USD',
    monedaCosto: 'USD',
    nroFile: '',
    aerolinea: '',
    destino: '',
    hotel: '',
    noches: 0,
    serviciosIncluidos: '',
    proveedor: '',
  });

  // Filtrar servicios por tipo
  const aereos = servicios.filter(s => s.tipo === 'Aéreo');
  const hoteles = servicios.filter(s => s.tipo === 'Hotel');
  const extras = servicios.filter(s => s.tipo === 'Extra');

  // Obtener índice original del servicio
  const getOriginalIndex = (servicio: ServicioExtended) => {
    return servicios.findIndex(s => s === servicio);
  };

  const handleToggleRevisado = (servicio: ServicioExtended, campo: 'revisado' | 'revisadoCosto', valor: boolean) => {
    const index = getOriginalIndex(servicio);
    if (onUpdateServicio && index !== -1) {
      const servicioActualizado = { ...servicio, [campo]: valor };
      onUpdateServicio(index, servicioActualizado, false, true);
    }
  };

  const handleEditarClick = (servicio: ServicioExtended, tipo: 'costo' | 'precio') => {
    const index = getOriginalIndex(servicio);
    if (index !== -1) {
      setServicioEditando({ index, servicio: { ...servicio }, tipo });
      setModalAbierto(true);
    }
  };

  const handleEliminarClick = (servicio: ServicioExtended) => {
    const index = getOriginalIndex(servicio);
    if (index !== -1) {
      setServicioAEliminar({ index, servicio });
      setModalEliminarAbierto(true);
    }
  };

  const confirmarEliminacion = () => {
    if (servicioAEliminar && onDeleteServicio) {
      onDeleteServicio(servicioAEliminar.index, servicioAEliminar.servicio);
    }
    setModalEliminarAbierto(false);
    setServicioAEliminar(null);
  };

  const handleCampoChange = (campo: string, valor: any) => {
    if (servicioEditando) {
      if (campo === 'proveedorId') {
        const selected = proveedores.find((p) => p.id === Number(valor));
        setServicioEditando({
          ...servicioEditando,
          servicio: {
            ...servicioEditando.servicio,
            proveedorId: Number(valor),
            proveedor: selected?.nombre || servicioEditando.servicio.proveedor,
          }
        });
        return;
      }

      setServicioEditando({
        ...servicioEditando,
        servicio: { ...servicioEditando.servicio, [campo]: valor }
      });
    }
  };

  const handleGuardarEdicion = async () => {
    if (!modalAbierto || !servicioEditando) return;
    if (!onUpdateServicio && !onPersistCostoEdit) return;

    const servicioOriginal = servicios[servicioEditando.index];
    
    const cambioRevisado = 
      servicioOriginal.revisado !== servicioEditando.servicio.revisado ||
      servicioOriginal.revisadoCosto !== servicioEditando.servicio.revisadoCosto;

    const mismosDatos =
      servicioOriginal.descripcion === servicioEditando.servicio.descripcion &&
      servicioOriginal.precioVenta === servicioEditando.servicio.precioVenta &&
      servicioOriginal.precioCosto === servicioEditando.servicio.precioCosto &&
      servicioOriginal.moneda === servicioEditando.servicio.moneda &&
      servicioOriginal.monedaCosto === servicioEditando.servicio.monedaCosto &&
      servicioOriginal.nroFile === servicioEditando.servicio.nroFile;
    
    const soloRevisado = cambioRevisado && mismosDatos;
    
    const cambioEnMontos = 
      servicioOriginal.precioVenta !== servicioEditando.servicio.precioVenta ||
      servicioOriginal.precioCosto !== servicioEditando.servicio.precioCosto ||
      servicioOriginal.moneda !== servicioEditando.servicio.moneda ||
      servicioOriginal.monedaCosto !== servicioEditando.servicio.monedaCosto;

    const esEdicionDeCosto = servicioEditando.tipo === 'costo';

    if (esEdicionDeCosto && onPersistCostoEdit) {
      setGuardandoModalCosto(true);
      try {
        await onPersistCostoEdit(servicioEditando.index, servicioEditando.servicio, cambioEnMontos);
        setModalAbierto(false);
        setServicioEditando(null);
      } finally {
        setGuardandoModalCosto(false);
      }
      return;
    }

    onUpdateServicio?.(servicioEditando.index, servicioEditando.servicio, cambioEnMontos, soloRevisado);
    setModalAbierto(false);
    setServicioEditando(null);
  };

  const isCostoListoParaEnviar = (): boolean => {
    if (!servicioEditando || servicioEditando.tipo !== 'costo') return false;
    const proveedorId = servicioEditando.servicio.proveedorId;
    const hasProviderId = Number.isFinite(Number(proveedorId)) && Number(proveedorId) > 0;
    const hasProviderName = (servicioEditando.servicio.proveedor || '').toString().trim().length > 0;
    const hasFile = (servicioEditando.servicio.nroFile || '').toString().trim().length > 0;
    const hasCosto = Number(servicioEditando.servicio.precioCosto || 0) > 0;
    return (hasProviderId || hasProviderName) && hasFile && hasCosto;
  };

  const handleCancelar = () => {
    setModalAbierto(false);
    setServicioEditando(null);
  };

  // Funciones para agregar nuevo servicio
  const handleAbrirModalAgregar = (tipo: 'Aéreo' | 'Hotel' | 'Extra', modo: 'costo' | 'precio') => {
    setTipoAgregar(tipo);
    setModoAgregar(modo);
    setNuevoServicio({
      tipo,
      descripcion: '',
      precioVenta: 0,
      precioCosto: 0,
      moneda: 'USD',
      monedaCosto: 'USD',
      nroFile: '',
      aerolinea: '',
      destino: '',
      hotel: '',
      noches: 0,
      serviciosIncluidos: '',
      proveedor: '',
    });
    setModalAgregarAbierto(true);
  };

  const handleNuevoServicioCampo = (campo: string, valor: any) => {
    setNuevoServicio(prev => ({ ...prev, [campo]: valor }));
  };

  const handleGuardarNuevoServicio = async () => {
    if (!onAddServicio && !onPersistNuevoCosto) return;
    
    // Construir descripción según tipo
    let descripcion = '';
    if (tipoAgregar === 'Aéreo') {
      descripcion = [nuevoServicio.aerolinea, nuevoServicio.destino, nuevoServicio.descripcion].filter(Boolean).join(' - ') || 'Vuelo';
    } else if (tipoAgregar === 'Hotel') {
      descripcion = nuevoServicio.descripcion || [nuevoServicio.hotel || 'Hotel', nuevoServicio.destino, nuevoServicio.noches ? `${nuevoServicio.noches} noches` : '', nuevoServicio.serviciosIncluidos].filter(Boolean).join(' - ');
    } else if (tipoAgregar === 'Extra') {
      descripcion = nuevoServicio.descripcion || 'Servicio adicional';
    }

    const servicioCompleto: ServicioExtended = {
      tipo: tipoAgregar,
      descripcion: descripcion,
      precioVenta: nuevoServicio.precioVenta || 0,
      precioCosto: nuevoServicio.precioCosto || 0,
      moneda: nuevoServicio.moneda || 'USD',
      monedaCosto: nuevoServicio.monedaCosto || 'USD',
      nroFile: nuevoServicio.nroFile || '',
      revisado: false,
      revisadoCosto: false,
      // Datos adicionales
      aerolinea: nuevoServicio.aerolinea,
      destino: nuevoServicio.destino,
      hotel: nuevoServicio.hotel,
      noches: nuevoServicio.noches,
      serviciosIncluidos: nuevoServicio.serviciosIncluidos,
      proveedor: nuevoServicio.proveedor,
      proveedorId: proveedores.find(p => p.nombre === nuevoServicio.proveedor)?.id,
      fechaViaje: nuevoServicio.fechaViaje || fechaViaje,
    };

    if (modoAgregar === 'costo' && onPersistNuevoCosto) {
      setGuardandoModalCosto(true);
      try {
        await onPersistNuevoCosto(servicioCompleto);
      } finally {
        setGuardandoModalCosto(false);
      }
    } else {
      onAddServicio?.(servicioCompleto);
    }
    setModalAgregarAbierto(false);
  };

  // Componente para renderizar una sección de servicios
  const renderSeccion = (
    titulo: string,
    listaServicios: ServicioExtended[],
    tipoSeccion: 'Aéreo' | 'Hotel' | 'Extra',
    colorChip: 'primary' | 'success' | 'warning',
    esListadoCostos: boolean
  ) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#461264' }}>
        {titulo}
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, textAlign: 'center', width: '5%' }}>#</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '40%' }}>Descripción</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'center', width: '15%' }}>Revisado</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '25%' }} align="right">
                {esListadoCostos ? 'Precio Costo' : 'Precio Venta'}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'center', width: '15%' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {listaServicios.length > 0 ? listaServicios.map((servicio, idx) => (
              <TableRow key={servicio.id || idx}>
                <TableCell sx={{ textAlign: 'center' }}>{idx + 1}</TableCell>
                <TableCell>{servicio.descripcion}</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  <Switch
                    checked={Boolean(esListadoCostos ? servicio.revisadoCosto : servicio.revisado)}
                    onChange={(e) => handleToggleRevisado(
                      servicio, 
                      esListadoCostos ? 'revisadoCosto' : 'revisado', 
                      e.target.checked
                    )}
                    size="small"
                    disabled={!isAdmin}
                  />
                </TableCell>
                <TableCell align="right">
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatearMoneda(
                        (esListadoCostos ? servicio.precioCosto : servicio.precioVenta) || 0, 
                        esListadoCostos ? (servicio.monedaCosto || 'USD') : (servicio.moneda || 'USD')
                      )}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleEditarClick(servicio, esListadoCostos ? 'costo' : 'precio')}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  {onDeleteServicio && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleEliminarClick(servicio)}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary" variant="body2">
                    No hay {titulo.toLowerCase()} registrados
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Botón Agregar - available in both Costos and Precios */}
      {onAddServicio && (
        <Box sx={{ mt: 1 }}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleAbrirModalAgregar(tipoSeccion, esListadoCostos ? 'costo' : 'precio')}
            sx={{ 
              backgroundColor: '#461264', 
              color: 'white',
              '&:hover': { backgroundColor: '#5a1a7a' },
              width: 32,
              height: 32
            }}
          >
            <AddCircleOutline fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );

  return (
    <>
        {/* LISTADO DE COSTOS */}
      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>LISTADO DE COSTOS</Typography>
          
          {renderSeccion('Aéreos', aereos.filter(s => (s.precioCosto || 0) > 0), 'Aéreo', 'primary', true)}
          {renderSeccion('Hoteles', hoteles.filter(s => (s.precioCosto || 0) > 0), 'Hotel', 'success', true)}
          {renderSeccion('Servicios', extras.filter(s => (s.precioCosto || 0) > 0), 'Extra', 'warning', true)}
        </Paper>
      </Grid>

      {/* LISTADO DE PRECIOS */}
      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>LISTADO DE PRECIOS</Typography>
          
          {renderSeccion('Aéreos', aereos.filter(s => (s.precioVenta || 0) > 0), 'Aéreo', 'primary', false)}
          {renderSeccion('Hoteles', hoteles.filter(s => (s.precioVenta || 0) > 0), 'Hotel', 'success', false)}
          {renderSeccion('Servicios', extras.filter(s => (s.precioVenta || 0) > 0), 'Extra', 'warning', false)}
        </Paper>
      </Grid>

      {/* MODAL DE EDICIÓN */}
      <Dialog open={modalAbierto} onClose={handleCancelar} maxWidth="sm" fullWidth>
        <DialogTitle>
          Editar {servicioEditando?.tipo === 'costo' ? 'Costo' : 'Precio'} de Servicio
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Descripción"
              fullWidth
              value={servicioEditando?.servicio.descripcion || ''}
              onChange={(e) => handleCampoChange('descripcion', e.target.value)}
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(servicioEditando?.tipo === 'costo' ? servicioEditando?.servicio.revisadoCosto : servicioEditando?.servicio.revisado)}
                  onChange={(e) => {
                    const campo = servicioEditando?.tipo === 'costo' ? 'revisadoCosto' : 'revisado';
                    handleCampoChange(campo, e.target.checked);
                  }}
                  disabled={!isAdmin}
                />
              }
              label="Revisado"
            />
            
            {servicioEditando?.tipo === 'costo' && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>
                  Datos de Costo
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl sx={{ minWidth: 120 }}>
                    <InputLabel>Moneda Costo</InputLabel>
                    <Select
                      value={servicioEditando?.servicio.monedaCosto || 'USD'}
                      onChange={(e) => handleCampoChange('monedaCosto', e.target.value)}
                      label="Moneda Costo"
                    >
                      <MenuItem value="USD">USD</MenuItem>
                      <MenuItem value="ARS">ARS</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <TextField
                    label="Monto Costo"
                    type="number"
                    fullWidth
                    value={servicioEditando?.servicio.precioCosto || 0}
                    onChange={(e) => handleCampoChange('precioCosto', parseFloat(e.target.value) || 0)}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Box>
                
                <TextField
                  label="Número de FILE"
                  fullWidth
                  value={servicioEditando?.servicio.nroFile || ''}
                  onChange={(e) => handleCampoChange('nroFile', e.target.value)}
                  placeholder="Opcional"
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Fecha de Viaje"
                    type="date"
                    fullWidth
                    value={
                      (servicioEditando?.servicio.fechaViaje)
                        ? new Date(servicioEditando?.servicio.fechaViaje).toISOString().slice(0, 10)
                        : fechaViaje
                          ? new Date(fechaViaje).toISOString().slice(0, 10)
                          : ''
                    }
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => handleCampoChange('fechaViaje', e.target.value || null)}
                    helperText="Por defecto usa la fecha de salida del paquete"
                  />

                  <Autocomplete
                    options={proveedores}
                    getOptionLabel={(option) => option.nombre}
                    value={
                      proveedores.find((p) => p.id === servicioEditando?.servicio.proveedorId) || null
                    }
                    onChange={(_, value) => {
                      if (!value) {
                        handleCampoChange('proveedorId', null);
                        handleCampoChange('proveedor', '');
                        return;
                      }
                      handleCampoChange('proveedorId', value.id);
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderOption={(props, option) => (
                      <li {...props} key={`${option.id}-${option.nombre}`}>
                        {option.nombre}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Proveedor"
                        placeholder="Buscar proveedor..."
                      />
                    )}
                    fullWidth
                    clearOnEscape
                  />
                </Box>
              </>
            )}
            
            {servicioEditando?.tipo === 'precio' && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>
                  Datos de Venta
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl sx={{ minWidth: 120 }}>
                    <InputLabel>Moneda Venta</InputLabel>
                    <Select
                      value={servicioEditando?.servicio.moneda || 'USD'}
                      onChange={(e) => handleCampoChange('moneda', e.target.value)}
                      label="Moneda Venta"
                    >
                      <MenuItem value="USD">USD</MenuItem>
                      <MenuItem value="ARS">ARS</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <TextField
                    label="Monto Venta"
                    type="number"
                    fullWidth
                    value={servicioEditando?.servicio.precioVenta || 0}
                    onChange={(e) => handleCampoChange('precioVenta', parseFloat(e.target.value) || 0)}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelar}>Cancelar</Button>
          <Button 
            onClick={handleGuardarEdicion}
            variant="contained"
            disabled={guardandoModalCosto}
            sx={{ backgroundColor: '#461264', '&:hover': { backgroundColor: '#5a1a7a' } }}
          >
            {guardandoModalCosto
              ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Guardando...</>
              : isCostoListoParaEnviar()
                ? 'Guardar y Enviar'
                : 'Guardar Cambios'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL DE AGREGAR SERVICIO */}
      <Dialog open={modalAgregarAbierto} onClose={() => setModalAgregarAbierto(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Agregar {tipoAgregar === 'Aéreo' ? 'Aéreo' : tipoAgregar === 'Hotel' ? 'Hotel' : 'Servicio Extra'} ({modoAgregar === 'costo' ? 'Costo' : 'Precio'})
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            
            {/* Campos para AÉREO */}
            {tipoAgregar === 'Aéreo' && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Autocomplete
                      freeSolo
                      options={AEROLINEAS}
                      value={nuevoServicio.aerolinea || ''}
                      onInputChange={(_, val) => handleNuevoServicioCampo('aerolinea', val)}
                      renderInput={(params) => <TextField {...params} label="Aerolínea" fullWidth />}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Autocomplete
                      freeSolo
                      options={DESTINOS}
                      value={nuevoServicio.destino || ''}
                      onInputChange={(_, val) => handleNuevoServicioCampo('destino', val)}
                      renderInput={(params) => <TextField {...params} label="Destino" fullWidth />}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Descripción del vuelo"
                      fullWidth
                      value={nuevoServicio.descripcion || ''}
                      onChange={(e) => handleNuevoServicioCampo('descripcion', e.target.value)}
                      placeholder="Ej: SCL-GIG-SCL"
                    />
                  </Grid>
                </Grid>
                
                {/* Precio de Venta - solo si modo es 'precio' */}
                {modoAgregar === 'precio' && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Precio de Venta</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <FormControl fullWidth>
                          <InputLabel>Moneda</InputLabel>
                          <Select
                            value={nuevoServicio.moneda || 'USD'}
                            onChange={(e) => handleNuevoServicioCampo('moneda', e.target.value)}
                            label="Moneda"
                          >
                            <MenuItem value="USD">USD</MenuItem>
                            <MenuItem value="ARS">ARS</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={9}>
                        <TextField
                          label="Precio de Venta"
                          type="number"
                          fullWidth
                          value={nuevoServicio.precioVenta || 0}
                          onChange={(e) => handleNuevoServicioCampo('precioVenta', parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
                
                {/* Datos de Costo - solo si modo es 'costo' */}
                {modoAgregar === 'costo' && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Datos de Costo</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Autocomplete
                          freeSolo
                          options={proveedores.map(p => p.nombre)}
                          value={nuevoServicio.proveedor || ''}
                          onInputChange={(_, val) => handleNuevoServicioCampo('proveedor', val)}
                          renderInput={(params) => <TextField {...params} label="Proveedor" fullWidth />}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <FormControl fullWidth>
                          <InputLabel>Moneda</InputLabel>
                          <Select
                            value={nuevoServicio.monedaCosto || 'USD'}
                            onChange={(e) => handleNuevoServicioCampo('monedaCosto', e.target.value)}
                            label="Moneda"
                          >
                            <MenuItem value="USD">USD</MenuItem>
                            <MenuItem value="ARS">ARS</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          label="Precio Costo"
                          type="number"
                          fullWidth
                          value={nuevoServicio.precioCosto || 0}
                          onChange={(e) => handleNuevoServicioCampo('precioCosto', parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                    </Grid>
                    
                    <TextField
                      label="Nro. de FILE"
                      value={nuevoServicio.nroFile || ''}
                      onChange={(e) => handleNuevoServicioCampo('nroFile', e.target.value)}
                      placeholder="Opcional"
                    />

                    <TextField
                      label="Fecha de Viaje"
                      type="date"
                      value={
                        nuevoServicio.fechaViaje
                          ? new Date(nuevoServicio.fechaViaje).toISOString().slice(0, 10)
                          : fechaViaje
                            ? new Date(fechaViaje).toISOString().slice(0, 10)
                            : ''
                      }
                      onChange={(e) => handleNuevoServicioCampo('fechaViaje', e.target.value || null)}
                      InputLabelProps={{ shrink: true }}
                      helperText="Por defecto usa la fecha de salida del paquete"
                    />
                  </>
                )}
              </>
            )}

            {/* Campos para HOTEL */}
            {tipoAgregar === 'Hotel' && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <TextField
                      label="Hotel"
                      fullWidth
                      value={nuevoServicio.hotel || ''}
                      onChange={(e) => handleNuevoServicioCampo('hotel', e.target.value)}
                      placeholder="Ej: Sheraton"
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Autocomplete
                      freeSolo
                      options={DESTINOS}
                      value={nuevoServicio.destino || ''}
                      onInputChange={(_, val) => handleNuevoServicioCampo('destino', val)}
                      renderInput={(params) => <TextField {...params} label="Destino" fullWidth />}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="# Noches"
                      type="number"
                      fullWidth
                      value={nuevoServicio.noches || 0}
                      onChange={(e) => handleNuevoServicioCampo('noches', parseInt(e.target.value) || 0)}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Servicios incluidos"
                      fullWidth
                      value={nuevoServicio.serviciosIncluidos || ''}
                      onChange={(e) => handleNuevoServicioCampo('serviciosIncluidos', e.target.value)}
                      placeholder="Ej: Desayuno inc."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Descripción opcional"
                      fullWidth
                      value={nuevoServicio.descripcion || ''}
                      onChange={(e) => handleNuevoServicioCampo('descripcion', e.target.value)}
                      placeholder="Ej: Hotel Sheraton Miami 6 noches all inclusive"
                    />
                  </Grid>
                </Grid>
                
                {/* Precio de Venta - solo si modo es 'precio' */}
                {modoAgregar === 'precio' && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Precio de Venta</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <FormControl fullWidth>
                          <InputLabel>Moneda</InputLabel>
                          <Select
                            value={nuevoServicio.moneda || 'USD'}
                            onChange={(e) => handleNuevoServicioCampo('moneda', e.target.value)}
                            label="Moneda"
                          >
                            <MenuItem value="USD">USD</MenuItem>
                            <MenuItem value="ARS">ARS</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={9}>
                        <TextField
                          label="Precio de Venta"
                          type="number"
                          fullWidth
                          value={nuevoServicio.precioVenta || 0}
                          onChange={(e) => handleNuevoServicioCampo('precioVenta', parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
                
                {/* Datos de Costo - solo si modo es 'costo' */}
                {modoAgregar === 'costo' && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Datos de Costo</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Autocomplete
                          freeSolo
                          options={Array.from(new Set(proveedores.map(p => p.nombre)))}
                          value={nuevoServicio.proveedor || ''}
                          onInputChange={(_, val) => handleNuevoServicioCampo('proveedor', val)}
                          renderInput={(params) => <TextField {...params} label="Proveedor" fullWidth />}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <FormControl fullWidth>
                          <InputLabel>Moneda</InputLabel>
                          <Select
                            value={nuevoServicio.monedaCosto || 'USD'}
                            onChange={(e) => handleNuevoServicioCampo('monedaCosto', e.target.value)}
                            label="Moneda"
                          >
                            <MenuItem value="USD">USD</MenuItem>
                            <MenuItem value="ARS">ARS</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          label="Precio Costo"
                          type="number"
                          fullWidth
                          value={nuevoServicio.precioCosto || 0}
                          onChange={(e) => handleNuevoServicioCampo('precioCosto', parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                    </Grid>
                    
                    <TextField
                      label="Nro. de FILE"
                      value={nuevoServicio.nroFile || ''}
                      onChange={(e) => handleNuevoServicioCampo('nroFile', e.target.value)}
                      placeholder="Opcional"
                    />

                    <TextField
                      label="Fecha de Viaje"
                      type="date"
                      value={
                        nuevoServicio.fechaViaje
                          ? new Date(nuevoServicio.fechaViaje).toISOString().slice(0, 10)
                          : fechaViaje
                            ? new Date(fechaViaje).toISOString().slice(0, 10)
                            : ''
                      }
                      onChange={(e) => handleNuevoServicioCampo('fechaViaje', e.target.value || null)}
                      InputLabelProps={{ shrink: true }}
                      helperText="Por defecto usa la fecha de salida del paquete"
                    />
                  </>
                )}
              </>
            )}

            {/* Campos para SERVICIO EXTRA */}
            {tipoAgregar === 'Extra' && (
              <>
                <TextField
                  label="Servicio"
                  fullWidth
                  value={nuevoServicio.descripcion || ''}
                  onChange={(e) => handleNuevoServicioCampo('descripcion', e.target.value)}
                  placeholder="Ej: Excursión a Cristo R."
                />
                
                {/* Precio de Venta - solo si modo es 'precio' */}
                {modoAgregar === 'precio' && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Precio de Venta</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <FormControl fullWidth>
                          <InputLabel>Moneda</InputLabel>
                          <Select
                            value={nuevoServicio.moneda || 'USD'}
                            onChange={(e) => handleNuevoServicioCampo('moneda', e.target.value)}
                            label="Moneda"
                          >
                            <MenuItem value="USD">USD</MenuItem>
                            <MenuItem value="ARS">ARS</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={9}>
                        <TextField
                          label="Precio de Venta"
                          type="number"
                          fullWidth
                          value={nuevoServicio.precioVenta || 0}
                          onChange={(e) => handleNuevoServicioCampo('precioVenta', parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
                
                {/* Datos de Costo - solo si modo es 'costo' */}
                {modoAgregar === 'costo' && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Datos de Costo</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Autocomplete
                          freeSolo
                          options={Array.from(new Set(proveedores.map(p => p.nombre)))}
                          value={nuevoServicio.proveedor || ''}
                          onInputChange={(_, val) => handleNuevoServicioCampo('proveedor', val)}
                          renderInput={(params) => <TextField {...params} label="Proveedor" fullWidth />}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <FormControl fullWidth>
                          <InputLabel>Moneda</InputLabel>
                          <Select
                            value={nuevoServicio.monedaCosto || 'USD'}
                            onChange={(e) => handleNuevoServicioCampo('monedaCosto', e.target.value)}
                            label="Moneda"
                          >
                            <MenuItem value="USD">USD</MenuItem>
                            <MenuItem value="ARS">ARS</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          label="Precio Costo"
                          type="number"
                          fullWidth
                          value={nuevoServicio.precioCosto || 0}
                          onChange={(e) => handleNuevoServicioCampo('precioCosto', parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                    </Grid>
                    
                    <TextField
                      label="Nro. de FILE"
                      value={nuevoServicio.nroFile || ''}
                      onChange={(e) => handleNuevoServicioCampo('nroFile', e.target.value)}
                      placeholder="Opcional"
                    />

                    <TextField
                      label="Fecha de Viaje"
                      type="date"
                      value={
                        nuevoServicio.fechaViaje
                          ? new Date(nuevoServicio.fechaViaje).toISOString().slice(0, 10)
                          : fechaViaje
                            ? new Date(fechaViaje).toISOString().slice(0, 10)
                            : ''
                      }
                      onChange={(e) => handleNuevoServicioCampo('fechaViaje', e.target.value || null)}
                      InputLabelProps={{ shrink: true }}
                      helperText="Por defecto usa la fecha de salida del paquete"
                    />
                  </>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalAgregarAbierto(false)}>Cancelar</Button>
          <Button 
            onClick={handleGuardarNuevoServicio}
            variant="contained"
            disabled={guardandoModalCosto}
            sx={{ backgroundColor: '#461264', '&:hover': { backgroundColor: '#5a1a7a' } }}
          >
            {guardandoModalCosto
              ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Guardando...</>
              : (modoAgregar === 'costo' ? 'Agregar y Enviar' : 'Agregar Servicio')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL CONFIRMAR ELIMINAR */}
      <Dialog open={modalEliminarAbierto} onClose={() => setModalEliminarAbierto(false)}>
        <DialogTitle>Eliminar Servicio</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro que deseas eliminar el servicio "{servicioAEliminar?.servicio.descripcion}"?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Esta acción se guardará de forma definitiva al dar clic en "Guardar Cambios" desde la ficha.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalEliminarAbierto(false)}>Cancelar</Button>
          <Button onClick={confirmarEliminacion} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
